import { useState, useMemo, useEffect, useRef } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight, Grid3X3 } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { motion, AnimatePresence } from "motion/react";
import { loadAllGalleryImages, type GalleryImage } from "../utils/simpleImageLoader";

export function Gallery() {
  // Load all images dynamically from assets folder
  const allImages = useMemo(() => loadAllGalleryImages(), []);
  
  // Show only first 6 images in the grid
  const displayImages = useMemo(() => allImages.slice(0, 6), [allImages]);
  
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);

  // Handle image click - open carousel at clicked image index
  const handleImageClick = (clickedImage: GalleryImage) => {
    const imageIndex = allImages.findIndex(img => img.id === clickedImage.id);
    setCurrentImageIndex(imageIndex);
    setIsCarouselOpen(true);
  };

  // Carousel navigation
  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Auto-scroll thumbnail into view when current image changes
  useEffect(() => {
    if (thumbnailScrollRef.current && isCarouselOpen) {
      const container = thumbnailScrollRef.current;
      const thumbnails = container.querySelectorAll('button');
      const currentThumbnail = thumbnails[currentImageIndex];
      
      if (currentThumbnail) {
        currentThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [currentImageIndex, isCarouselOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isCarouselOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        case 'Escape':
          event.preventDefault();
          setIsCarouselOpen(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isCarouselOpen]);

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-gray-50" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-16"
        >
          <h2 id="gallery-heading" className="text-[#0f1f3d] mb-4 text-4xl lg:text-5xl">
            Explore our facilities
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            A look inside the rooms, shared spaces and grounds at Majesty Hostel.
          </p>
          {allImages.length > 6 && (
            <p className="text-sm text-gray-500 mt-4">
              Showing {displayImages.length} of {allImages.length} photos — tap any image to view all
            </p>
          )}
        </motion.div>

        {/* Masonry Grid - Show only first 6 images */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {displayImages.map((image: GalleryImage, index: number) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="break-inside-avoid group cursor-pointer"
              onClick={() => handleImageClick(image)}
            >
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500">
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1f3d]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 flex items-center justify-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1 }}
                    className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white"
                  >
                    <ZoomIn className="w-8 h-8 text-white" />
                  </motion.div>
                </div>

                {/* Show "+X more" on last image if there are more images */}
                {index === displayImages.length - 1 && allImages.length > displayImages.length && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                    <div className="text-center text-white">
                      <Grid3X3 className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-lg font-semibold">+{allImages.length - displayImages.length} more</p>
                      <p className="text-sm opacity-80">Click to view all</p>
                    </div>
                  </div>
                )}

                {/* Image */}
                <div className={`relative w-full ${image.tall ? 'h-96' : 'h-64'} overflow-hidden`}>
                  <ImageWithFallback
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>

                {/* Label */}
                <div className="absolute bottom-0 left-0 right-0 p-4 z-20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-white font-medium">{image.alt}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Carousel Modal */}
      <AnimatePresence>
        {isCarouselOpen && allImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50"
            onClick={() => setIsCarouselOpen(false)}
          >
            {/* Close Button */}
            <button
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors z-50"
              onClick={() => setIsCarouselOpen(false)}
              aria-label="Close gallery"
            >
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            </button>

            {/* Image Counter */}
            <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 z-50">
              <span className="text-white text-sm font-medium">
                {currentImageIndex + 1} / {allImages.length}
              </span>
            </div>

            {/* Main Image */}
            <div className="absolute inset-0 flex items-center justify-center p-4 pb-24">
              <motion.div
                key={currentImageIndex}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="relative max-w-6xl max-h-full w-full"
                onClick={(e) => e.stopPropagation()}
              >
              <ImageWithFallback
                src={allImages[currentImageIndex].src}
                alt={allImages[currentImageIndex].alt}
                className="w-full h-full object-contain rounded-2xl"
              />
              
                {/* Image Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
                  <p className="text-white text-xl">{allImages[currentImageIndex].alt}</p>
                </div>
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            {allImages.length > 1 && (
              <>
                <button
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors z-40"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="w-6 h-6 text-white" aria-hidden="true" />
                </button>

                <button
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors z-40"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  aria-label="Next photo"
                >
                  <ChevronRight className="w-6 h-6 text-white" aria-hidden="true" />
                </button>
              </>
            )}

            {/* Thumbnail Strip */}
            <div 
              ref={thumbnailScrollRef}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm rounded-full p-2 z-50 overflow-x-scroll gallery-thumbnail-scroll"
              style={{ 
                maxWidth: '95vw',
                display: 'flex',
                gap: '0.5rem'
              }}
              onClick={(e) => e.stopPropagation()}
              onWheel={(e) => {
                e.stopPropagation();
                if (thumbnailScrollRef.current) {
                  thumbnailScrollRef.current.scrollLeft += e.deltaY;
                }
              }}
            >
              {allImages.map((image, index) => (
                <button
                  key={image.id}
                  className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                    index === currentImageIndex
                      ? 'ring-2 ring-white scale-110'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToImage(index);
                  }}
                  aria-label={`View photo ${index + 1}`}
                  aria-current={index === currentImageIndex}
                >
                  <ImageWithFallback
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
